import { Dayjs } from '@seolens/date-manager';
import { Button, Form } from 'antd';
import { FC, ReactElement, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage, AppModal } from '@/common/components';
import { AppModalControlProps, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  PagesBulkPublishAction,
  PagesBulkPublishFormFields,
  PagesBulkPublishFormInputs,
  PagesBulkPublishScope,
  PagesQueryKeys,
  PageUsageForBots,
  PageUsageForKeys,
  PageUsageForType,
  useBulkPublishPagesMutation,
  useGetPagesBulkPublishListQuery,
} from '@/modules/Pages';

import { PagesBulkPublishModalView } from './PagesBulkPublishModalView';
import { pagesBulkPublishFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  action: PagesBulkPublishAction;
  controls: AppModalControlProps;
}

export const PagesBulkPublishModalContainer: FC<Props> = ({ siteId, action, controls }) => {
  const appMessageApi = AppMessage.useControls();
  const queryClient = useQueryClient();

  const [form] = Form.useForm<PagesBulkPublishFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PagesBulkPublishFormInputs>>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const scope = Form.useWatch<PagesBulkPublishScope | undefined>(PagesBulkPublishFormInputs.Scope, { form, preserve: true });
  const usageForVariants = Form.useWatch<PageUsageForType[] | undefined>(
    PagesBulkPublishFormInputs.UsageForVariants,
    { form, preserve: true },
  );
  const pageIds = Form.useWatch<string[] | undefined>(PagesBulkPublishFormInputs.PageIds, { form, preserve: true });

  const {
    data: pagesListData,
    error: pagesListError,
    isLoading: isPagesListLoading,
    isFetching: isPagesListFetching,
    isError: isPagesListError,
  } = useGetPagesBulkPublishListQuery(siteId, {
    page: currentPage,
    isPublished: action === 'unpublish',
    usageForVariants: usageForVariants ?? [],
  })({ enabled: controls.isOpen && scope === 'selected' && !!usageForVariants?.length });

  const { mutate: bulkPublishPagesMutation, isLoading: bulkPublishPagesIsLoading } = useBulkPublishPagesMutation(siteId, action)({
    onSuccess: async ({ updatedCount }) => {
      if (updatedCount) {
        appMessageApi.success(
          action === 'publish' ? `Опубліковано сторінок: ${updatedCount}` : `Знято з публікації сторінок: ${updatedCount}`,
        );
      } else {
        appMessageApi.info('Немає сторінок, до яких можна застосувати цю дію');
      }

      await queryClient.invalidateQueries([PagesQueryKeys.GetAll], { exact: false });
      await queryClient.invalidateQueries([PagesQueryKeys.GetOneById], { exact: false });
      await queryClient.invalidateQueries([PagesQueryKeys.GetOneByIdDossier], { exact: false });
      await queryClient.invalidateQueries([PagesQueryKeys.BulkPublishList], { exact: false });

      controls.close();
    },
    onError: (error) => {
      setFormErrors(extractFormErrors<typeof PagesBulkPublishFormInputs>(error));
      messageOnError(error, appMessageApi);
    },
  });

  const initialValues: PagesBulkPublishFormFields = {
    scope: 'all',
    usageForVariants: [{ type: 'default' }, { [PageUsageForKeys.Bot]: PageUsageForBots.Google }],
    pageIds: [],
    publishedAt: undefined,
  };

  const handleFinish = (values: PagesBulkPublishFormFields) => {
    setFormErrors(null);

    bulkPublishPagesMutation({
      publishedAt: action === 'publish' ? (values.publishedAt as Dayjs).toISOString() : undefined,
      scope: values.scope as PagesBulkPublishScope,
      pageIds: values.scope === 'selected' ? ((values.pageIds as string[] | undefined) ?? []) : undefined,
      usageForVariants: values.usageForVariants as PageUsageForType[],
    });
  };

  const handleFinishFailed = () => {
    setFormErrors(null);
  };

  const handleValuesChange = (changedValues: Partial<PagesBulkPublishFormFields>) => {
    if (PagesBulkPublishFormInputs.Scope in changedValues || PagesBulkPublishFormInputs.UsageForVariants in changedValues) {
      form.setFieldValue(PagesBulkPublishFormInputs.PageIds, []);
      setCurrentPage(1);
    }
  };

  const handleModalClose = () => {
    form.resetFields();
    setFormErrors(null);
    setCurrentPage(1);
  };

  return (
    <AppModal
      width={1200}
      title={action === 'publish' ? 'Публікація сторінок' : 'Зняття сторінок з публікації'}
      footer={
        !isPagesListError && <>
          <Button disabled={bulkPublishPagesIsLoading} onClick={controls.close}>
            Скасувати
          </Button>

          <Button
            type="primary"
            loading={bulkPublishPagesIsLoading}
            disabled={isPagesListLoading || isPagesListError}
            onClick={form.submit}
          >
            {action === 'publish' ? 'Опублікувати' : 'Зняти з публікації'}
            {scope === 'selected' && <>{`(${pageIds?.length ?? 0})`}</>}
          </Button>
        </>
      }
      centered
      closable={!bulkPublishPagesIsLoading}
      maskClosable={!bulkPublishPagesIsLoading}
      controls={controls}
      afterClose={handleModalClose}
      styles={{ body: { minHeight: 380 } }}
    >
      <FallbackedLayout data={pagesListData} isLoading={isPagesListLoading} isError={isPagesListError} error={pagesListError}>
        {(pagesList): ReactElement => (
          <PagesBulkPublishModalView
            action={action}
            scope={scope}
            usageForVariants={usageForVariants}
            currentPage={currentPage}
            pagesList={pagesList}
            isPagesListFetching={isPagesListFetching}
            formName={`pages-bulk-${action}-form`}
            formType="edit"
            form={form}
            formErrors={formErrors}
            initialValues={initialValues}
            isFormLoading={bulkPublishPagesIsLoading}
            validationSchema={pagesBulkPublishFormValidationSchema}
            handleBack={controls.close}
            handleFinish={handleFinish}
            handleFinishFailed={handleFinishFailed}
            handleValuesChange={handleValuesChange}
            handleChangePage={setCurrentPage}
          />
        )}
      </FallbackedLayout>
    </AppModal>
  );
};
